import React, { useState, useEffect, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import '../../Assets/Style/style.css';
import LeaseApiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { FaRegEye, FaTrash, FaSave } from 'react-icons/fa';
import { HiPencil } from 'react-icons/hi2';
import { IoAddSharp } from 'react-icons/io5';
import { RiRefreshLine } from 'react-icons/ri';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getName } from '../../../Redux/selecters';

// Industry Of Buyer master (api/general/industryofbuyer on the Lease Finance backend).
// Backend contract: { status, data, message } - on failure (HTTP 400) the error text
// comes back in `data`, not `message` (SH_ApiResponseVM(false, "...")).
const getErrorText = (error, fallback) => {
  const body = error?.response?.data;
  if (typeof body?.data === 'string') return body.data;
  return body?.message || fallback;
};

const IndustryOfBuyerListing = () => {
  const apiService = new LeaseApiService();
  const userName = useSelector(getName);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState({ mode: null, data: null }); // mode: null | 'create' | 'edit' | 'view'
  const [industryDesc, setIndustryDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = JSON.parse(localStorage.getItem('RoleAccess')) || [];
    setPermissions(storedPermissions.find((acc) => acc.formName?.toLowerCase() === 'industryofbuyer') || {});
    getItems();
  }, []);

  const getItems = async () => {
    try {
      setLoading(true);
      const res = await apiService.get('INDUSTRY_OF_BUYER_LIST');
      setRowData(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error('Error fetching industry of buyer list:', error);
      toast.error(getErrorText(error, 'Failed to load Industry Of Buyer list'));
    } finally {
      setLoading(false);
    }
  };

  const openForm = (mode, data = null) => {
    setIndustryDesc(data?.industry_Desc || '');
    setFormModal({ mode, data });
  };
  const closeForm = () => {
    if (saving) return;
    setFormModal({ mode: null, data: null });
    setIndustryDesc('');
  };

  const handleSave = async () => {
    const desc = industryDesc.trim();
    if (!desc) {
      toast.warning('Please Enter Industry Description');
      return;
    }
    const isEdit = formModal.mode === 'edit';
    const body = {
      record_Id: isEdit ? String(formModal.data?.record_Id) : '0',
      industry_Desc: desc,
      modify_By: userName || '',
      modify_On: new Date().toISOString(),
    };
    try {
      setSaving(true);
      const res = isEdit
        ? await apiService.putParameter('INDUSTRY_OF_BUYER_UPDATE', encodeURIComponent(body.record_Id), body)
        : await apiService.post('INDUSTRY_OF_BUYER_CREATE', body);
      if (res?.data?.status) {
        toast.success(isEdit ? 'Industry Of Buyer updated successfully' : 'Industry Of Buyer created successfully');
        setSaving(false);
        setFormModal({ mode: null, data: null });
        setIndustryDesc('');
        getItems();
      } else {
        toast.warning(getErrorText({ response: res }, 'Save failed'));
      }
    } catch (error) {
      console.error('Error saving industry of buyer:', error);
      toast.error(getErrorText(error, 'Failed to save Industry Of Buyer'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow?.record_Id) return;
    try {
      setDeleting(true);
      const res = await apiService.deleteParameter('INDUSTRY_OF_BUYER_DELETE', encodeURIComponent(deleteRow.record_Id));
      if (res?.data?.status) {
        toast.success('Industry Of Buyer deleted successfully');
        setDeleteRow(null);
        getItems();
      } else {
        toast.warning(getErrorText({ response: res }, 'Delete failed'));
      }
    } catch (error) {
      console.error('Error deleting industry of buyer:', error);
      toast.error(getErrorText(error, 'Failed to delete Industry Of Buyer'));
    } finally {
      setDeleting(false);
    }
  };

  const columnDefs = [
    {
      headerName: '',
      field: 'record_Id',
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        onView: permissions?.canView === true ? (data) => openForm('view', data) : null,
        onEdit: permissions?.canUpdate === true ? (data) => openForm('edit', data) : null,
        onDelete: permissions?.canDelete === true ? (data) => setDeleteRow(data) : null,
      },
      pinned: 'left',
      width: 110,
      sortable: false,
      filter: false,
    },
    { headerName: 'ID', field: 'record_Id', filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
    { headerName: 'Industry Description', field: 'industry_Desc', filter: true, floatingFilter: true, suppressMovable: true, flex: 1, minWidth: 250 },
    { headerName: 'Modified By', field: 'modify_By', filter: true, floatingFilter: true, suppressMovable: true, width: 180 },
    {
      headerName: 'Modified On',
      field: 'modify_On',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      width: 180,
      valueFormatter: (params) => (params.value ? moment(params.value).format('DD-MMM-YYYY') : ''),
    },
  ];

  const isView = formModal.mode === 'view';
  const modalTitle = formModal.mode === 'create' ? 'Create Industry Of Buyer' : isView ? 'View Industry Of Buyer' : 'Edit Industry Of Buyer';

  return (
    <div className="p-3">
      <div className="border-box2 p-3">
        <div className="d-flex justify-contant-between">
          <h5 className="col mainInvHead">Industry Of Buyer</h5>
          <div className="d-flex justify-content-end gap-2">
            <div onClick={() => getItems()} className="mainbtnGry flex-end gap-2">
              <RiRefreshLine color="#fff" className="center mt-1" />
              <div>Refresh</div>
            </div>
            {permissions?.canAdd === true && (
              <button className="mainbtn" type="button" onClick={() => openForm('create')}>
                <IoAddSharp color="#fff" className="center mt-1" /> <div>Add</div>
              </button>
            )}
          </div>
        </div>
        <div className="gridshad mt-2">
          <div className="ag-theme-quartz" style={{ height: 650 }}>
            {loading ? (
              <div className="center p-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : (
              <AgGridReact
                getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                rowData={rowData}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={20}
              />
            )}
          </div>
        </div>
      </div>

      {/* Create / Edit / View */}
      <Modal show={!!formModal.mode} onHide={closeForm} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title as="h6">{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formModal.mode !== 'create' && (
            <div className="d-flex mb-3">
              <label className="form-label pt-1 start">ID</label>
              <input className="p-2 form-control w-75" value={formModal.data?.record_Id || ''} disabled />
            </div>
          )}
          <div className="d-flex mb-3">
            <label className="form-label pt-1 start">Industry Description *</label>
            <input
              type="text"
              className="p-2 form-control w-75"
              value={industryDesc}
              onChange={(e) => setIndustryDesc(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isView && handleSave()}
              disabled={isView}
              autoFocus={!isView}
            />
          </div>
          {isView && (
            <>
              <div className="d-flex mb-3">
                <label className="form-label pt-1 start">Modified By</label>
                <input className="p-2 form-control w-75" value={formModal.data?.modify_By || ''} disabled />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">Modified On</label>
                <input
                  className="p-2 form-control w-75"
                  value={formModal.data?.modify_On ? moment(formModal.data.modify_On).format('DD-MMM-YYYY hh:mm A') : ''}
                  disabled
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="end p-3">
          <button className="mainbtnSecondary" type="button" onClick={closeForm} disabled={saving}>
            {isView ? 'Close' : 'Cancel'}
          </button>
          {!isView && (
            <button className="mainbtn flex-end gap-2" type="button" onClick={handleSave} disabled={saving}>
              {saving ? <ClipLoader color="#ffffff" size={18} /> : <FaSave color="#fff" className="center mt-1" />}
              <div>Save</div>
            </button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete confirm */}
      <Modal show={!!deleteRow} onHide={() => !deleting && setDeleteRow(null)} centered>
        <Modal.Body className="text-center p-4">
          <p className="mb-4">Do you want to delete &quot;{deleteRow?.industry_Desc}&quot;?</p>
          <div className="d-flex justify-content-center gap-3">
            <button type="button" className="btn btn-danger px-4" disabled={deleting} onClick={confirmDelete}>
              {deleting ? <ClipLoader color="#ffffff" size={16} /> : 'Delete'}
            </button>
            <button type="button" className="btn btn-secondary px-4" disabled={deleting} onClick={() => setDeleteRow(null)}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

class BtnCellRenderer extends Component {
  render() {
    const { data, onView, onEdit, onDelete } = this.props;
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn?.(data);
    };
    return (
      <div className="unit-row-actions">
        {onView && <FaRegEye color="#6E5199" size={16} title="View" onClick={stop(onView)} />}
        {onEdit && <HiPencil color="#313a46" size={16} title="Edit" onClick={stop(onEdit)} />}
        {onDelete && <FaTrash color="#DC3545" size={14} title="Delete" onClick={stop(onDelete)} />}
      </div>
    );
  }
}

export default IndustryOfBuyerListing;
